/**----------------------------------------------------------------------------------------------------
 * Draws the legend.
 *
 * European Union Public Licence V. 1.2
 * EUPL © the European Union 2007, 2016
 *
 * Modified: 2024, Eddy Scheper, OpenGeoGroep/ARIS BV
 *           2026, Eddy Scheper, ARIS BV
 */

import {createFeatureBox, createFeaturePoint, createFeatureRect, createFeatureText, createFillStyle} from "./OLUtils";
import {ProfileLayout} from "./ProfileLayout";

/**-----------------------------------------------------------------------------
 */
export default class ProfileLegend {

  // Main app.
  app = null;

  // Legend margin.
  legendMargin = 1;

  // Title font, height and margin.
  titleFont = "";
  titleFontSize = 1;
  titleHeight = 0;
  titleMargin = 0;

  // Box font.
  boxFont = "";
  boxFontSize = 1;

  // Box width and height.
  boxWidth = 9;
  boxHeight = 3;

  // Box margins.
  boxMarginTop = 1;
  boxMarginRight = 1;

  /**-----------------------------------------------------------------------------
   */
  constructor(app) {
    let layout,pixelSize;

    this.app = app;

    layout = ProfileLayout.getLegendLayout();

    pixelSize = layout.pixelSize;

    this.titleFont = layout.legendTitleFont;
    this.titleFontSize = layout.legendTitleFontSize;

    this.titleHeight = this.titleFontSize * pixelSize;
    this.titleMargin = 0;

    this.boxFont = layout.legendBoxFont;
    this.boxFontSize = layout.legendBoxFontSize;

    this.legendMargin = this.boxFontSize * pixelSize;
  }
  /**-----------------------------------------------------------------------------
   * Called in ProfilePopup.drawLegends().
   * @param source
   * @param title
   * @param positionX
   * @param positionY
   * @param styleTable        - color or fill table.
   * @param showOutline       - for rasterfills.
   * @param onlyCalcPosition  - only calculate position, do not draw.
   */
  drawLegend(source,title,positionX,positionY,styleTable,showOutline,onlyCalcPosition){
    let features,feature,value,keys,style,text;
    let boxX,boxY,labelX,labelY,titleX,titleY,hWidth,hHeight,currentX,currentY;
    let i,len;

    features = [];

    // Get key values, from low to high.
    keys = styleTable.getKeys();

    console.log("#ProfileLegend.drawLegend() - "+keys.length);
    console.log(keys);

    //---------------------------------------------------------
    // Draw legend boxes and labels (from down to top).
    //---------------------------------------------------------

    hWidth = this.boxWidth / 2;
    hHeight = this.boxHeight / 2;

    boxX = positionX;
    boxY = positionY;

    console.log("--------------------------------------- LEGEND");
    console.log("### boxFont          : " + this.boxFont);
    console.log("### boxX,boxY        : " + boxX + " " + boxY);
    console.log("### boxWidth         : " + this.boxWidth);
    console.log("### boxHeight        : " + this.boxHeight);
    console.log("### boxMarginTop     : " + this.boxMarginTop);
    console.log("### boxMarginRight   : " + this.boxMarginRight);

    for (i = 0, len = keys.length; i < len; i++) {

      // Get value.
      value = keys[i];

      // Draw box fill.
      style = styleTable.getStyle(value)
      feature = createFeatureBox(boxX,boxY,this.boxWidth,this.boxHeight,style);
      features.push(feature);

      // Draw box outline.
      if (showOutline) {
        style = createFillStyle(null,"black");
        feature = createFeatureBox(boxX,boxY,this.boxWidth,this.boxHeight,style);
        features.push(feature);
      }

      // Draw box label.
      labelX = boxX + hWidth + this.boxMarginRight;
      labelY = boxY + hHeight;
      text = styleTable.getText(value)
      feature = createFeatureText(text,labelX,labelY,this.boxFont,"left","middle");
      features.push(feature);

      // DEBUG
      if (this.app.config.TEST_SHOW_LAYOUT_POINTS) {
        feature = createFeaturePoint(labelX,labelY,"black",1.5);
        features.push(feature);
      }

      boxY += this.boxHeight + this.boxMarginTop;
    }

    //---------------------------------------------------------
    // Draw title.
    //---------------------------------------------------------

    // Set title position.
    titleX = boxX - hWidth;
    titleY = boxY;

    // Add title margin.
    titleY += this.titleMargin;

    // For Testing.
    // if (this.test) {
    //   feature = createFeaturePoint(titleX,titleY,"black",1);
    //   features.push(feature);
    // }

    console.log("### titleFont        : " + this.titleFont);
    console.log("### titleX,titleY    : " + titleX + " " + titleY);
    console.log("### titleHeight      : " + this.titleHeight);
    console.log("### titleMargin      : " + this.titleMargin);

    // Draw title.
    feature = createFeatureText(title,titleX,titleY,this.titleFont,"left","bottom");
    features.push(feature);

    // Update title position.
    titleY += this.titleHeight;

    // Also draw the legend?
    if (!onlyCalcPosition) {
      // Add features.
      source.addFeatures(features);
    }

    // Set current position.
    currentX = boxX;
    currentY = titleY;

    // Add legend margin.
    currentY += this.legendMargin;

    console.log("### currentX,currentY: " + currentX + " " + currentY);
    console.log("---------------------------------------");

    return {
      currentX: currentX,
      currentY: currentY,
    }
  }
  /**-----------------------------------------------------------------------------
   * Called in FlightProfilePopup.drawLegend().
   * @param backSource
   * @param legendSource
   * @param layout
   * @param styleTable        - color or fill table.
   */
  drawFlightLegend(backSource,legendSource,layout,styleTable){
    let features,feature,value,keys,style,text;
    let boxX,boxY,labelX,labelY,hWidth,hHeight,currentX,currentY;
    let backgroundWidthLeft,backgroundWidthRight,backgroundHeight;
    let backgroundBoxX,backgroundBoxY;
    let backgroundColor,backgroundFeature;
    let i,len;

    // Get key values, from low to high.
    keys = styleTable.getKeys();

    console.log("### ProfileLegend.drawFlightLegend - "+keys.length);
    console.log(keys);

    //---------------------------------------------------------
    // Draw legend boxes and labels (from down to top).
    //---------------------------------------------------------

    hWidth = layout.legendBoxWidth / 2;
    hHeight = layout.legendBoxHeight / 2;

    // BoxX is center position.
    boxX = layout.legendPositionX;
    boxY = layout.legendPositionY;

    console.log("--------------------------------------- LEGEND");
    console.log("### boxFont             : " + layout.legendBoxFont);
    console.log("### boxX,boxY           : " + boxX + " " + boxY);
    console.log("### boxWidth            : " + layout.legendBoxWidth);
    console.log("### boxHeight           : " + layout.legendBoxHeight);
    console.log("### boxMarginTop        : " + layout.legendBoxMarginTop);
    console.log("### boxMarginRight      : " + layout.legendBoxMarginRight);

    // Get legend features.
    features = [];
    for (i = 0, len = keys.length; i < len; i++) {

      // Get value.
      value = keys[i];

      // Draw box fill.
      style = styleTable.getStyle(value);
      feature = createFeatureBox(boxX,boxY,layout.legendBoxWidth,layout.legendBoxHeight,style);
      features.push(feature);

      // Draw box label.
      labelX = boxX + hWidth + layout.legendBoxMarginRight;
      labelY = boxY + hHeight;
      text = styleTable.getText(value);
      feature = createFeatureText(text,labelX,labelY,layout.legendBoxFont,"left","middle");
      features.push(feature);

      boxY += layout.legendBoxHeight + layout.legendBoxMarginTop;
    }

    // Set the current position.
    currentX = boxX;
    currentY = boxY;

    backgroundWidthLeft = layout.legendWidthLeft;
    backgroundWidthRight = layout.legendWidthRight;
    backgroundHeight = currentY - layout.legendPositionY;

    // Add margin.
    backgroundWidthLeft += 2 * layout.legendBackgroundMargin;
    backgroundWidthRight += 2 * layout.legendBackgroundMargin;
    backgroundHeight += 2 * layout.legendBackgroundMargin;

    // Set background position and size.
    backgroundBoxX = layout.legendPositionX;
    backgroundBoxY = layout.legendPositionY - layout.legendBackgroundMargin;

    // Set background color.
    backgroundColor = "rgba(255,255,255," + layout.legendBackgroundOpacity + ")";

    // Create the background style.
    style = createFillStyle(backgroundColor,null,1);

    console.log("### currentX,currentY   : " + currentX + " " + currentY);
    console.log("### legendWidthLeft     : " + layout.legendWidthLeft);
    console.log("### legendWidthRight    : " + layout.legendWidthRight);
    console.log("### backgroundWidthRight: " + backgroundWidthRight);
    console.log("### backgroundMargin    : " + layout.legendBackgroundMargin);
    console.log("### backgroundX         : " + backgroundBoxX);
    console.log("### backgroundY         : " + backgroundBoxY);
    console.log("### backgroundWidthLeft : " + backgroundWidthLeft);
    console.log("### backgroundWidthRight: " + backgroundWidthRight);
    console.log("### backgroundHeight    : " + backgroundHeight);
    console.log("### backgroundColor     : " + backgroundColor);
    console.log("---------------------------------------");

    // Create background box fill.
    backgroundFeature = createFeatureRect(backgroundBoxX,backgroundBoxY,
                                          backgroundWidthLeft,backgroundWidthRight,backgroundHeight,style);
    backSource.clear();
    backSource.addFeatures([backgroundFeature]);

    // Add legend features.
    legendSource.clear();
    legendSource.addFeatures(features);
  }
}